import React, { useCallback, useMemo } from "react";
import { Clinician, Patient } from "../../../services/getFunctions";
// Importing icons
import { BiLock, BiBlock } from "react-icons/bi";
import { BsPeople, BsCheckCircle } from "react-icons/bs";
import { CgUserList } from "react-icons/cg";
import { FiEdit } from "react-icons/fi";
// Table-Components
import Table from "./Table";
import TableEditDropdownButton from "./TableEditDropdownButton";

/* Interface for ClinicianTable */
interface ClinicianTableProps {
  clinicians: Clinician[];
  patients: Patient[];
  openEditClinicianModal: (event: React.MouseEvent<HTMLElement>) => void;
  openShowPatientListModal: (event: React.MouseEvent<HTMLElement>) => void;
  openTransferPatientsModal: (event: React.MouseEvent<HTMLElement>) => void;
  disableEnableClinician: (event: React.MouseEvent<HTMLElement>) => void;
  resetPassword: (event: React.MouseEvent<HTMLElement>) => void;
}

/* Interface for EditDropdown in the edit column */
interface EditDropdownProps {
  index: number;
  modalID: string;
  clinician: {
    enabled: boolean;
  };
  openEditClinicianModal: (event: React.MouseEvent<HTMLButtonElement>) => void;
  openShowPatientListModal: (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => void;
  openTransferPatientsModal: (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => void;
  disableEnableClinician: (event: React.MouseEvent<HTMLButtonElement>) => void;
  resetPassword: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

/**
 * Defining what the edit-dropdown in this table should do
 *
 * @param index                     Row index for index of dropdown
 * @param modalID                   Identifier for modals
 * @param clinician                 Array of clinicians
 * @param openEditClinicianModal    UI Pop-up for editing clinician
 * @param openShowPatientListModal  UI Pop-up for showing all patient for a clinician
 * @param openTransferPatientsModal UI Pop-up for transfering patients from one clinician to another
 * @param disableEnableClinician    UI Pop-up for diabling/ enabling clinician
 * @param resetPassword             UI Pop-up for reseting password of that patient/ clinician
 */
const EditDropdown = ({
  index,
  modalID,
  clinician,
  openEditClinicianModal,
  openShowPatientListModal,
  openTransferPatientsModal,
  disableEnableClinician,
  resetPassword,
}: EditDropdownProps) => {
  const dropdownItems = [
    {
      key: "0",
      text: (
        <>
          <FiEdit /> Mitarbeitenden-ID ändern
        </>
      ),
      onClick: openEditClinicianModal,
    },
    {
      key: "1",
      text: (
        <>
          <BiLock /> Passwort zurücksetzen
        </>
      ),
      onClick: resetPassword,
    },
    {
      key: "2",
      text: (
        <>
          <CgUserList /> Patient*innenliste anzeigen
        </>
      ),
      onClick: openShowPatientListModal,
    },
    {
      key: "3",
      text: (
        <>
          <BsPeople /> Patient*innen transferieren
        </>
      ),
      onClick: openTransferPatientsModal,
    },
    clinician.enabled
      ? {
          key: "4",
          text: (
            <>
              <BiBlock /> Mitarbeitende*n deaktivieren
            </>
          ),
          onClick: disableEnableClinician,
        }
      : {
          key: "5",
          text: (
            <>
              <BsCheckCircle /> Mitarbeitende*n aktivieren
            </>
          ),
          onClick: disableEnableClinician,
        },
  ];

  return (
    <TableEditDropdownButton
      index={index}
      modalID={modalID}
      items={dropdownItems}
    />
  );
};

/**
 * Displays a table of clinicians with various functionalities such as sorting, filtering, and pagination.
 *
 * @param clinicians Arrays of clinicians
 * @param patients Arrays of patients
 * @param openEditClinicianModal UI Pop-up for editing clinician
 * @param openShowPatientListModal UI Pop-up for showing all patient for a clinician
 * @param openTransferPatientsModal UI Pop-up for transfering all patients from one clinician to another
 * @param disableEnableClinician UI Pop-up for diabling/ enabling clinician
 * @param resetPassword UI Pop-up for reseting password of that patient/ clinician
 */
const ClinicianTable = ({
  clinicians,
  patients,
  openEditClinicianModal,
  openShowPatientListModal,
  openTransferPatientsModal,
  disableEnableClinician,
  resetPassword,
}: ClinicianTableProps) => {
  // Counts the number of patients assigned to a given clinician.
  const countPatient = useCallback(
    (clinician: Clinician) => {
      return patients.filter((patient) => patient.clinician_id === clinician.id)
        .length;
    },
    [patients],
  );

  // Define table columns with cell rendering
  const columns = useMemo(
    () => [
      {
        accessorKey: "edit",
        header: "",
        enableColumnFilter: false,
        enableSorting: false,
        customCSS: { width: "70px" },
      },
      {
        accessorKey: "username",
        header: "Mitarbeitenden-ID",
        enableSorting: true,
        sortingFn: (rowA, rowB, columnId: string) => {
          return rowA.original[columnId].props?.children >
            rowB.original[columnId].props?.children
            ? 1
            : rowA.original[columnId].props?.children <
                rowB.original[columnId].props?.children
              ? -1
              : 0;
        },
        enableColumnFilter: true,
        filterFn: "includesReactElement",
      },
      {
        accessorKey: "password",
        header: "Passwort",
      },
      {
        accessorKey: "patientCount",
        header: "Zugewiesene Patient*innen",
        enableSorting: true,
        sortingFn: (rowA, rowB, columnId: string) => {
          return rowA.original[columnId].props?.children >
            rowB.original[columnId].props?.children
            ? 1
            : rowA.original[columnId].props?.children <
                rowB.original[columnId].props?.children
              ? -1
              : 0;
        },
        enableColumnFilter: true,
        filterFn: "includesReactElement",
      },
    ],
    [],
  );

  // Generate data for the table rows - use RAW VALUES for filtering
  const data = useMemo(
    () =>
      clinicians.map((clinician, index) => ({
        edit: (
          <EditDropdown
            {...{
              index,
              modalID: String(index),
              clinician,
              openEditClinicianModal,
              openShowPatientListModal,
              openTransferPatientsModal,
              disableEnableClinician,
              resetPassword,
            }}
          />
        ),

        username: (
          <span
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              ...(clinician.enabled ? {} : { color: "gray" }),
            }}
          >
            <span className="material-symbols-rounded fs-5">
              {clinician.enabled ? "person" : "person_off"}
            </span>{" "}
            {clinician.username}
          </span>
        ),
        password: (
          <span style={!clinician.enabled ? { color: "gray" } : {}}>
            ******
          </span>
        ),
        patientCount: (
          <span style={!clinician.enabled ? { color: "gray" } : {}}>
            {countPatient(clinician)}
          </span>
        ),
      })),
    [
      clinicians,
      countPatient,
      disableEnableClinician,
      openEditClinicianModal,
      openShowPatientListModal,
      openTransferPatientsModal,
      resetPassword,
    ],
  );

  return <Table columnData={columns} rowData={data} />;
};

export default ClinicianTable;
